/**
 * Vista "Varianza" (RES-47): teórico vs. contado por insumo. La varianza en unidades es la suma
 * de las diferencias de los COUNT del rango (lo mismo que registró cada conteo); los faltantes van
 * en rojo. Gráfico divergente del top N en $ (faltante / sobrante) y tabla completa.
 */
import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import HelpIcon from '../help/HelpIcon';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  formatCompactCurrency,
  formatPct,
  formatQty,
  money,
  varianceTop,
} from '../../utils/inventoryReports';
import {
  ChartLegend,
  ChartTooltip,
  KpiTiles,
  MoneyNote,
  SortableTable,
  axisProps,
} from './ReportParts';

const ROW_HEIGHT = 32;

const signClass = (value) => (value < 0 ? 'is-negative' : value > 0 ? 'is-positive' : '');

function VarianceReport({ data, t, locale, colors, nameOf, includeUncounted, onIncludeUncounted }) {
  const items = useMemo(() => data?.items || [], [data]);
  const totals = data?.totals || {};
  const consolidated = Boolean(data?.consolidated);
  // En el consolidado el mismo insumo aparece una vez por local
  const top = useMemo(
    () =>
      varianceTop(items, (item) =>
        consolidated ? `${item.name} · ${nameOf(item.restaurantId)}` : item.name
      ),
    [items, consolidated, nameOf]
  );

  const formatDate = (iso) =>
    iso
      ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
          new Date(iso)
        )
      : '—';

  const columns = [
    {
      key: 'name',
      label: t('inventory.reports.columns.item'),
      sortValue: (r) => r.name,
      render: (r) => (
        <>
          {r.name}
          {!r.counts && (
            <span className="report-tag">{t('inventory.reports.variance.notCounted')}</span>
          )}
        </>
      ),
    },
    ...(consolidated
      ? [
          {
            key: 'restaurant',
            label: t('inventory.reports.columns.restaurant'),
            sortValue: (r) => nameOf(r.restaurantId),
            render: (r) => nameOf(r.restaurantId),
          },
        ]
      : []),
    {
      key: 'theoretical',
      label: t('inventory.reports.columns.theoretical'),
      align: 'right',
      sortValue: (r) => r.theoretical,
      render: (r) => `${formatQty(r.theoretical, locale)} ${r.unit}`,
    },
    {
      key: 'actual',
      label: t('inventory.reports.columns.actual'),
      align: 'right',
      sortValue: (r) => r.actual,
      render: (r) => `${formatQty(r.actual, locale)} ${r.unit}`,
    },
    {
      key: 'variance',
      label: t('inventory.reports.columns.variance'),
      align: 'right',
      sortValue: (r) => r.variance,
      cellClass: (r) => signClass(Number(r.variance) || 0),
      render: (r) => `${r.variance > 0 ? '+' : ''}${formatQty(r.variance, locale)} ${r.unit}`,
    },
    {
      key: 'variancePct',
      label: t('inventory.reports.columns.variancePct'),
      align: 'right',
      sortValue: (r) => r.variancePct,
      cellClass: (r) => signClass(Number(r.variancePct) || 0),
      render: (r) => formatPct(r.variancePct, locale),
    },
    {
      key: 'varianceValue',
      label: t('inventory.reports.columns.varianceValue'),
      align: 'right',
      sortValue: (r) => money(r.varianceValue).value,
      cellClass: (r) => signClass(money(r.varianceValue).value),
      render: (r) => (
        <>
          {formatCurrency(money(r.varianceValue).value)}
          <MoneyNote amount={r.varianceValue} t={t} />
        </>
      ),
    },
    {
      key: 'lastCountAt',
      label: t('inventory.reports.columns.lastCountAt'),
      sortValue: (r) => r.lastCountAt,
      render: (r) =>
        r.counts > 1 ? `${formatDate(r.lastCountAt)} (${r.counts})` : formatDate(r.lastCountAt),
    },
  ];

  const shortage = money(totals.shortage);
  const net = money(totals.net);

  return (
    <div className="report-view" data-view="variance">
      <div className="report-view-toolbar">
        <label className="report-check">
          <input
            type="checkbox"
            checked={includeUncounted}
            onChange={(e) => onIncludeUncounted(e.target.checked)}
            data-testid="include-uncounted"
          />
          {t('inventory.reports.filters.includeUncounted')}
        </label>
        <span className="report-help-inline">
          {t('inventory.reports.variance.whatIs')}
          <HelpIcon topicKey="inventory.variance" />
        </span>
      </div>

      <KpiTiles
        tiles={[
          {
            key: 'shortage',
            label: t('inventory.reports.variance.kpiShortage'),
            value: formatCurrency(shortage.value),
            tone: shortage.value < 0 ? 'negative' : undefined,
            sub: <MoneyNote amount={totals.shortage} t={t} />,
          },
          {
            key: 'surplus',
            label: t('inventory.reports.variance.kpiSurplus'),
            value: formatCurrency(money(totals.surplus).value),
            sub: <MoneyNote amount={totals.surplus} t={t} />,
          },
          {
            key: 'net',
            label: t('inventory.reports.variance.kpiNet'),
            value: formatCurrency(net.value),
            tone: net.value < 0 ? 'negative' : undefined,
          },
          {
            key: 'counted',
            label: t('inventory.reports.variance.kpiCounted'),
            value: String(Number(totals.countedItems) || 0),
          },
        ]}
      />

      <section className="report-card">
        <div className="report-card-header">
          <h3>{t('inventory.reports.variance.chartTitle', { count: top.length })}</h3>
          <ChartLegend
            items={[
              {
                key: 'shortage',
                label: t('inventory.reports.variance.shortage'),
                color: colors.shortage,
              },
              {
                key: 'surplus',
                label: t('inventory.reports.variance.surplus'),
                color: colors.surplus,
              },
            ]}
          />
        </div>
        {top.length > 0 ? (
          <div
            className="report-chart"
            role="img"
            aria-label={t('inventory.reports.variance.chartTitle', { count: top.length })}
          >
            <ResponsiveContainer width="100%" height={top.length * ROW_HEIGHT + 40}>
              <BarChart
                data={top}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke={colors.grid} />
                <XAxis
                  type="number"
                  {...axisProps(colors)}
                  tickFormatter={(v) => formatCompactCurrency(v, locale)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={160}
                  {...axisProps(colors)}
                  tick={{ fill: colors.text, fontSize: 12 }}
                />
                <ReferenceLine x={0} stroke={colors.axis} />
                <Tooltip
                  cursor={{ fill: colors.grid, opacity: 0.4 }}
                  content={
                    <ChartTooltip
                      rows={(payload) => {
                        const row = payload[0].payload;
                        return [
                          {
                            key: 'value',
                            label: t(
                              row.value < 0
                                ? 'inventory.reports.variance.shortage'
                                : 'inventory.reports.variance.surplus'
                            ),
                            value: formatCurrency(row.value),
                            color: row.value < 0 ? colors.shortage : colors.surplus,
                          },
                        ];
                      }}
                    />
                  }
                />
                <Bar dataKey="value" barSize={16} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  {top.map((row) => (
                    <Cell key={row.key} fill={row.value < 0 ? colors.shortage : colors.surplus} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="report-hint">{t('inventory.reports.variance.noDifferences')}</p>
        )}
      </section>

      <section className="report-card">
        <h3>{t('inventory.reports.variance.tableTitle')}</h3>
        <p className="report-hint">{t('inventory.reports.variance.tableHint')}</p>
        <SortableTable
          t={t}
          testId="variance-table"
          columns={columns}
          rows={items}
          rowKey={(r) => `${r.restaurantId}|${r.itemId}`}
          defaultSort={{ key: 'varianceValue', direction: 'asc' }}
        />
      </section>
    </div>
  );
}

export default VarianceReport;
